import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { realtimeSyncService, SyncLog } from '../../../services/realtimeSyncService';
import { supabase } from '../../../lib/supabase';

interface SyncStatusIndicatorProps {
  groupId?: string;
}

const SyncStatusIndicator = ({ groupId }: SyncStatusIndicatorProps) => {
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [recentMobileActivity, setRecentMobileActivity] = useState(0);

  useEffect(() => {
    if (!groupId) {
      setIsConnected(false);
      return;
    }

    let mounted = true;

    // Set up connection state change callback
    realtimeSyncService.setConnectionStateChangeCallback((state) => {
      if (mounted) {
        setIsConnected(state === 'connected');
      }
    });

    // Set up callbacks
    realtimeSyncService.onSyncLogReceived = (log: SyncLog) => {
      if (log.platform === 'mobile' && mounted) {
        setRecentMobileActivity((prev) => prev + 1);
        setSyncLogs((prev) => [log, ...prev].slice(0, 10));
      }
    };

    realtimeSyncService.onContributionAdded = (contribution: any) => {
      // Check if contribution was from mobile
      if (contribution.recorded_by && mounted) {
        // Could check metadata or other indicators
        setRecentMobileActivity((prev) => prev + 1);
      }
    };

    // Subscribe to real-time updates and wait for result
    const subscribe = async () => {
      setIsConnected(false); // Start as disconnected
      const success = await realtimeSyncService.subscribeToGroup(groupId);
      if (mounted) {
        setIsConnected(success);
      }
      
      // Load recent sync logs after subscription
      if (success) {
        loadSyncLogs();
      }
    };

    subscribe();

    return () => {
      mounted = false;
      realtimeSyncService.unsubscribe();
      setIsConnected(false);
    };
  }, [groupId]);

  const loadSyncLogs = async () => {
    try {
      const logs = await realtimeSyncService.getSyncLogs(10);
      setSyncLogs(logs);
      const mobileLogs = logs.filter((log) => log.platform === 'mobile');
      setRecentMobileActivity(mobileLogs.length);
    } catch (error) {
      console.error('Failed to load sync logs:', error);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    return date.toLocaleTimeString();
  };

  return (
    <div className="bg-card border border-border rounded-md shadow-elevation-1 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-success animate-pulse' : 'bg-destructive'
            }`}
          />
          <h3 className="text-sm font-semibold text-foreground">
            Real-time Sync Status
          </h3>
        </div>
        {recentMobileActivity > 0 && (
          <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-sm">
            {recentMobileActivity} new
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Connection:</span>
          <span className={isConnected ? 'text-success' : 'text-destructive'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>

        {syncLogs.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-xs font-medium text-foreground mb-2">
              Recent Mobile Activity:
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {syncLogs
                .filter((log) => log.platform === 'mobile')
                .slice(0, 5)
                .map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center gap-2 text-xs text-muted-foreground"
                  >
                    <Icon name="Smartphone" size={12} />
                    <span className="flex-1">
                      {log.action.replace('_', ' ')}
                    </span>
                    <span>{formatTime(log.created_at)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {syncLogs.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-2">
            No recent mobile activity
          </p>
        )}
      </div>
    </div>
  );
};

export default SyncStatusIndicator;

